// 粒子类型
export type ParticleType = 
  | 'leaf'      // 落叶（大树点击）
  | 'coin'      // 金币粒子
  | 'heart'     // 爱心（奶奶互动）
  | 'water'     // 水花（水井点击）
  | 'feather'   // 羽毛（鸡点击）
  | 'flour'     // 面粉（石磨点击）
  | 'sparkle'   // 闪光
  | 'smoke';    // 烟雾

// 粒子实例
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  scale: number;
  life: number;      // 0-1
  decay: number;     // 每帧减少的生命值
  type: ParticleType;
  color: string;
  active: boolean;
}

export class ParticleSystem {
  private particles: Particle[] = [];
  private pool: Particle[] = [];
  private maxParticles: number = 100;
  
  // 粒子颜色配置
  private colors: Record<ParticleType, string[]> = {
    leaf: ['#FFD700', '#FFA500', '#FF8C00', '#90EE90'],
    coin: ['#FFD700', '#FFA500', '#FFF8DC'],
    heart: ['#FF6B6B', '#FF8E8E', '#FFB6C1'],
    water: ['#4682B4', '#87CEEB', '#B0E0E6'],
    feather: ['#FFFFFF', '#F5F5F5', '#E8E8E8'],
    flour: ['#FFF8DC', '#F5F5DC', '#FAEBD7'],
    sparkle: ['#FFD700', '#FFFACD', '#FFFFFF'],
    smoke: ['#FFFFFF', '#F0F0F0', '#E0E0E0'],
  };
  
  spawnBurst(x: number, y: number, sourceType: string): void {
    // 根据源类型决定粒子类型
    let particleType: ParticleType;
    let count: number;
    
    switch (sourceType) {
      case 'tree':
        particleType = 'leaf';
        count = 5 + Math.floor(Math.random() * 5);
        break;
      case 'grandma':
        particleType = 'heart';
        count = 2 + Math.floor(Math.random() * 2);
        break;
      case 'chicken':
        particleType = 'feather';
        count = 3 + Math.floor(Math.random() * 3);
        break;
      case 'well':
        particleType = 'water';
        count = 8 + Math.floor(Math.random() * 4);
        break;
      case 'mill':
        particleType = 'flour';
        count = 10 + Math.floor(Math.random() * 5);
        break;
      case 'house':
        particleType = 'smoke';
        count = 3 + Math.floor(Math.random() * 3);
        break;
      default:
        particleType = 'sparkle';
        count = 4 + Math.floor(Math.random() * 3);
    }
    
    for (let i = 0; i < count; i++) {
      this.spawn(x, y, particleType);
    }
  }
  
  spawn(x: number, y: number, type: ParticleType): void {
    if (this.particles.length >= this.maxParticles) {
      return;
    }
    
    let particle: Particle;
    
    // 从对象池获取或创建
    if (this.pool.length > 0) {
      particle = this.pool.pop()!;
    } else {
      particle = {
        x: 0, y: 0, vx: 0, vy: 0,
        rotation: 0, rotationSpeed: 0,
        scale: 1, life: 1, decay: 0.02,
        type: 'sparkle', color: '', active: false,
      };
    }
    
    // 初始化粒子
    particle.x = x + (Math.random() - 0.5) * 20;
    particle.y = y + (Math.random() - 0.5) * 20;
    particle.type = type;
    particle.active = true;
    particle.life = 1;
    particle.rotation = Math.random() * Math.PI * 2;
    
    // 根据类型设置属性
    const colors = this.colors[type];
    particle.color = colors[Math.floor(Math.random() * colors.length)];
    
    switch (type) {
      case 'leaf':
        particle.vx = (Math.random() - 0.5) * 60;
        particle.vy = -30 - Math.random() * 50;
        particle.rotationSpeed = (Math.random() - 0.5) * 4;
        particle.decay = 0.008 + Math.random() * 0.004;
        particle.scale = 0.5 + Math.random() * 0.5;
        break;
        
      case 'coin':
        particle.vx = (Math.random() - 0.5) * 100;
        particle.vy = -80 - Math.random() * 60;
        particle.rotationSpeed = (Math.random() - 0.5) * 6;
        particle.decay = 0.015 + Math.random() * 0.01;
        particle.scale = 0.3 + Math.random() * 0.3;
        break;
        
      case 'heart':
        particle.vx = (Math.random() - 0.5) * 40;
        particle.vy = -50 - Math.random() * 30;
        particle.rotationSpeed = (Math.random() - 0.5) * 2;
        particle.decay = 0.01;
        particle.scale = 0.6 + Math.random() * 0.4;
        break;
        
      case 'water':
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1;
        const speed = 80 + Math.random() * 60;
        particle.vx = Math.cos(angle) * speed;
        particle.vy = Math.sin(angle) * speed;
        particle.rotationSpeed = (Math.random() - 0.5) * 3;
        particle.decay = 0.02;
        particle.scale = 0.2 + Math.random() * 0.3;
        break;
        
      case 'feather':
        particle.vx = (Math.random() - 0.5) * 30;
        particle.vy = -20 - Math.random() * 30;
        particle.rotationSpeed = (Math.random() - 0.5) * 3;
        particle.decay = 0.006;
        particle.scale = 0.4 + Math.random() * 0.4;
        break;
        
      case 'flour':
        particle.vx = (Math.random() - 0.5) * 50;
        particle.vy = -40 - Math.random() * 40;
        particle.rotationSpeed = (Math.random() - 0.5) * 2;
        particle.decay = 0.008;
        particle.scale = 0.3 + Math.random() * 0.3;
        break;
        
      case 'sparkle':
        particle.vx = (Math.random() - 0.5) * 60;
        particle.vy = -40 - Math.random() * 40;
        particle.rotationSpeed = Math.random() * 4;
        particle.decay = 0.02 + Math.random() * 0.01;
        particle.scale = 0.2 + Math.random() * 0.3;
        break;
        
      case 'smoke':
        particle.vx = (Math.random() - 0.5) * 20;
        particle.vy = -30 - Math.random() * 20;
        particle.rotationSpeed = (Math.random() - 0.5) * 1;
        particle.decay = 0.005;
        particle.scale = 0.5 + Math.random() * 0.5;
        break;
    }
    
    this.particles.push(particle);
  }
  
  update(dt: number): void {
    this.particles = this.particles.filter(particle => {
      if (!particle.active) return false;
      
      // 更新位置
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.rotation += particle.rotationSpeed * dt;
      
      // 应用物理效果
      switch (particle.type) {
        case 'leaf':
        case 'feather':
          // 轻盈飘落，受风力影响
          particle.vy += 20 * dt;  // 轻重力
          particle.vx += Math.sin(Date.now() * 0.003 + particle.y * 0.01) * 10 * dt;
          break;
          
        case 'water':
          // 水花受重力下落
          particle.vy += 150 * dt;
          break;
          
        case 'flour':
        case 'smoke':
          // 面粉和烟雾缓慢飘散
          particle.vy *= 0.98;
          particle.vx *= 0.98;
          particle.scale += dt * 0.3;
          break;
          
        case 'heart':
          // 爱心左右摇摆上升
          particle.vx = Math.sin(Date.now() * 0.005 + particle.y * 0.02) * 30;
          break;
          
        default:
          // 默认重力
          particle.vy += 50 * dt;
      }
      
      // 更新生命值
      particle.life -= particle.decay;
      
      if (particle.life <= 0) {
        particle.active = false;
        this.pool.push(particle);
        return false;
      }
      
      return true;
    });
  }
  
  render(ctx: CanvasRenderingContext2D): void {
    this.particles.forEach(particle => {
      this.renderParticle(ctx, particle);
    });
  }
  
  private renderParticle(ctx: CanvasRenderingContext2D, particle: Particle): void {
    ctx.save();
    ctx.translate(particle.x, particle.y);
    ctx.rotate(particle.rotation);
    ctx.scale(particle.scale, particle.scale);
    ctx.globalAlpha = particle.life;
    
    switch (particle.type) {
      case 'leaf':
        this.renderLeaf(ctx, particle.color);
        break;
      case 'coin':
        this.renderCoin(ctx, particle.color);
        break;
      case 'heart':
        this.renderHeart(ctx, particle.color);
        break;
      case 'water':
        this.renderWater(ctx, particle.color);
        break;
      case 'feather':
        this.renderFeather(ctx, particle.color);
        break;
      case 'flour':
      case 'smoke':
        this.renderCloud(ctx, particle.color);
        break;
      case 'sparkle':
        this.renderSparkle(ctx, particle.color);
        break;
    }
    
    ctx.restore();
  }
  
  private renderLeaf(ctx: CanvasRenderingContext2D, color: string): void {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(0, 0, 8, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-6, 0);
    ctx.lineTo(6, 0);
    ctx.stroke();
  }
  
  private renderCoin(ctx: CanvasRenderingContext2D, color: string): void {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(0, 0, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#B8860B';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  
  private renderHeart(ctx: CanvasRenderingContext2D, color: string): void {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, 4);
    ctx.bezierCurveTo(-6, -2, -6, -6, 0, -8);
    ctx.bezierCurveTo(6, -6, 6, -2, 0, 4);
    ctx.fill();
  }
  
  private renderWater(ctx: CanvasRenderingContext2D, color: string): void {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(0, 0, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  
  private renderFeather(ctx: CanvasRenderingContext2D, color: string): void {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(0, 0, 10, 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#DDD';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-8, 0);
    ctx.lineTo(8, 0);
    ctx.stroke();
  }
  
  private renderCloud(ctx: CanvasRenderingContext2D, color: string): void {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(0, 0, 8, 0, Math.PI * 2);
    ctx.arc(6, -2, 6, 0, Math.PI * 2);
    ctx.arc(-6, -2, 6, 0, Math.PI * 2);
    ctx.fill();
  }
  
  private renderSparkle(ctx: CanvasRenderingContext2D, color: string): void {
    ctx.fillStyle = color;
    // 十字星形状
    const size = 6;
    ctx.beginPath();
    ctx.moveTo(0, -size);
    ctx.quadraticCurveTo(2, -2, size, 0);
    ctx.quadraticCurveTo(2, 2, 0, size);
    ctx.quadraticCurveTo(-2, 2, -size, 0);
    ctx.quadraticCurveTo(-2, -2, 0, -size);
    ctx.fill();
  }
  
  // 获取活跃粒子数量（用于调试）
  getActiveCount(): number {
    return this.particles.length;
  }
  
  // 设置最大粒子数
  setMaxParticles(max: number): void {
    this.maxParticles = max;
  }
}
